import React, { useMemo, useState, useRef } from "react";
import Input from "../../../modules/Input";
import Link from "../../../modules/Link";
import Card from "../../../modules/Card";
import api_requests from "../../../resources/api_requests";
import Spinner from "../../../modules/Spinner";
import ImageLinks from "../../../assets/Images/ImagesLink";
import { useTranslation } from "react-i18next";

const PersonalDetailsScreen = ({ onNext, selectedCountry }) => {
  const [errors, setErrors] = useState({});
  const [isFormValid, setFormValidation] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const businessNameRef = useRef();
  const emailRef = useRef();
  const contactRef = useRef();
  const termsAndConditionsRef = useRef();
  const { t } = useTranslation();

  const form = useMemo(
    () => [
      {
        name: "firstName",
        label: `${t("first_name")}*`,
        placeholder: t("enter_first_name"),
        ref: firstNameRef,
        type: "text",
      },
      {
        name: "lastName",
        label: `${t("last_name")}*`,
        placeholder: t("enter_last_name"),
        ref: lastNameRef,
        type: "text",
      },
      {
        name: "businessName",
        label: `${t("biz_name")}*`,
        placeholder: t("enter_biz_name"),
        ref: businessNameRef,
        type: "text",
      },
      {
        name: "email",
        label: "Email*",
        placeholder: t("email_placeholder"),
        ref: emailRef,
        type: "email",
      },
      {
        name: "contact",
        label: `${t("mobile_number")}*`,
        placeholder: t("enter_mobile_number"),
        ref: contactRef,
        type: "number",
      },
    ],
    [],
  );

  const allowDigitsOnly = (e) => {
    const value = e.target.value;

    if (/^\d{0,10}$/.test(value)) {
      e.target.value = value;
    } else {
      e.target.value = value.replace(/\D/g, "").slice(0, 10);
    }
  };

  const validators = {
    firstName: (value) => {
      if (!value.trim()) return t("first_name_req");
      return "";
    },
    lastName: (value) => {
      if (!value.trim()) return t("last_name_req");
      return "";
    },
    businessName: (value) => {
      if (!value.trim()) return t("biz_name_req");
      return "";
    },
    email: (value) => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) return t("email_req");
      if (!emailPattern.test(value)) return t("valid_email");
      return "";
    },
    contact: (value) => {
      if (!value.trim()) return t("mobile_required");
      if (!/^\d+$/.test(value)) return t("only_digits");
      if (selectedCountry === "india") {
        if (value.length !== 10) return "Contact must be exactly 10 digits";
      } else {
        if (value.length > 12) {
          return "Contact cannot exceed 12 digits";
        } else if (!value.startsWith("8")) {
          return "Please enter valid contact number";
        }
      }
      return "";
    },
  };

  const validateForm = async () => {
    let isValid = true;
    const newErrors = {};
    const termsAndConditions = isChecked;

    form.forEach((field) => {
      const value = field.ref.current.value;
      const fieldError = validators[field.name](value);
      if (fieldError) {
        newErrors[field.name] = fieldError;
        isValid = false;
      }
    });

    if (!termsAndConditions) {
      newErrors["termsAndConditions"] = t("this_field_req");
      isValid = false;
    }

    let doesUserExists = false;
    if (selectedCountry === "india" && !("contact" in newErrors)) {
      try {
        const res = await api_requests.auth_status({
          mobile: contactRef.current?.value,
        });
        doesUserExists = !!res?.data?.is_password_login_enabled;
      } catch (e) {}
    }

    if (doesUserExists) {
      newErrors["contact"] = t("conatct_already_exist");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!(await validateForm())) {
      setLoading(false);
      return;
    }

    const formData = {};
    form.forEach((field) => {
      formData[field.name] = field.ref.current.value;
    });

    if (selectedCountry === "india") {
      api_requests
        .request_otp({
          mobile: formData.contact,
        })
        .then((res) => {
          // MARK::- HANDLE ERROR HERE
          console.log(res);
          onNext(formData);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (selectedCountry === "indonesia") {
      api_requests
        .request_otp({
          email: formData.email,
        })
        .then((res) => {
          console.log(res);
          onNext(formData);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  function handleChange() {
    let isValid = true;
    const termsAndConditions = isChecked;

    form.forEach((field) => {
      const value = field.ref.current.value;
      const fieldError = validators[field.name](value);
      if (fieldError) {
        isValid = false;
      }
    });

    if (!termsAndConditions) {
      isValid = false;
    }

    if (isValid) {
      setFormValidation(true);
    } else {
      setFormValidation(false);
    }
  }

  return (
    <div className="px-6 pt-10">
      <div className="flex flex-col items-center gap-0.5 justify-center">
        <p className="font-custom  text-2xl font-semibold text-[#7953D2] ">
          {t("sign_up")}!
        </p>
        <p className="font-custom text-sm text-xs font-medium text-dark_gray ">
          {t("get_started")}
        </p>
      </div>

      <Card>
        <div className="flex flex-col gap-[20px]">
          {form.map(({ name, label, placeholder, ref, type }) => {
            return (
              <div key={name}>
                <Input
                  label={label}
                  placeholder={placeholder}
                  ref={ref}
                  onChange={(e) => {
                    if (name === "contact") {
                      allowDigitsOnly(e);
                    }
                    handleChange({ name, ref });
                  }}
                  disabled={isLoading}
                  inputType={type}
                />
                {errors[name] && (
                  <p className="text-red-700 text-[10px] font-semibold font-custom mt-1">
                    {errors[name]}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col mt-[17px] font-custom">
          <div className="flex" onClick={() => setIsChecked((prev) => !prev)}>
            <span
              className={`flex items-center justify-center w-4 h-4 rounded border ${
                isChecked
                  ? "bg-dark_purple border-dark_purple"
                  : "bg-white border-gray"
              }`}
            >
              {isChecked && (
                <img src={ImageLinks.check} className="w-4 h-4" alt="check" />
              )}
            </span>
            <div className="ml-[4px] text-stone-900 text-xs font-semibold font-custom">
              Agree to{" "}
              <Link to="https://pidge.in/terms-of-use" target="_blank">
                Terms of Conditions
              </Link>{" "}
              and{" "}
              <Link to="https://pidge.in/privacy-policy" target="_blank">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div>
            {errors["termsAndConditions"] && (
              <p className="text-red-700 text-[10px] font-semibold font-custom mt-1">
                {errors["termsAndConditions"]}
              </p>
            )}
          </div>
        </div>

        <div className="mt-[34px]">
          <button
            onClick={handleSubmit}
            className="font-custom btn btn-block bg-[#492897] text-white rounded-[30px] text-base font-semibold disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Spinner heightResponsive />
            ) : (
              <span>{t("start")}</span>
            )}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default PersonalDetailsScreen;
